<template>
  <div>
    <!-- Load History Button -->
    <b-button
      @click="loadHistory"
      v-if="historyData === null"
      class="mb-2 mr-sm-2 mb-sm-0"
    >
      Carica storico firma (se disponibile)
    </b-button>

    <!-- History Content -->
    <div v-if="historyData !== null">
      <!-- Summary Cards -->
      <div v-if="showSummary" class="history-summary mb-4">
        <div class="row">
          <!-- Recipients Summary -->
          <div class="col-lg-6 mb-3">
            <div class="card border-primary">
              <div class="card-header bg-primary text-white">
                <h6 class="mb-0">
                  <i class="fas fa-users mr-2"></i>
                  Destinatari ({{ recipients.length }})
                </h6>
              </div>
              <div class="card-body">
                <div v-for="recipient in recipients" :key="recipient.id" class="recipient-item mb-2 pb-2 border-bottom">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{{ recipient.name }}</strong>
                      <br>
                      <small class="text-muted">{{ recipient.email }}</small>
                    </div>
                    <div>
                      <span v-if="recipient.hasSigned" class="badge badge-success">
                        <i class="fas fa-check-circle mr-1"></i>Firmato
                      </span>
                      <span v-else class="badge badge-warning">
                        <i class="fas fa-clock mr-1"></i>In attesa
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Timeline Summary -->
          <div class="col-lg-6 mb-3">
            <div class="card border-info">
              <div class="card-header bg-info text-white">
                <h6 class="mb-0">
                  <i class="fas fa-clock mr-2"></i>
                  Riepilogo Attività
                </h6>
              </div>
              <div class="card-body">
                <div v-if="lastNotificationDate" class="mb-3">
                  <strong>Ultima notifica inviata:</strong>
                  <br>
                  <span class="text-muted">{{ lastNotificationDate }}</span>
                </div>

                <div v-if="completionDate" class="mb-3">
                  <strong>Documento completato:</strong>
                  <br>
                  <span class="text-success">{{ completionDate }}</span>
                </div>

                <div v-if="stats" class="mt-3 pt-3 border-top">
                  <div class="row text-center">
                    <div class="col-6">
                      <div class="stat-box">
                        <h4 class="text-success mb-0">{{ stats.signed }}</h4>
                        <small class="text-muted">Firmati</small>
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="stat-box">
                        <h4 class="text-warning mb-0">{{ stats.pending }}</h4>
                        <small class="text-muted">In attesa</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Events Message -->
      <p v-if="!historyData || !historyData.length" class="text-muted">
        Storico non disponibile
      </p>

      <!-- Detailed Event Table -->
      <table
        v-else
        class="table table-hover table-bordered"
        style="max-width: 100%;table-layout: fixed"
      >
        <thead class="table-light">
          <tr>
            <th>Data</th>
            <th>Tipo</th>
            <th>Completato</th>
            <th>Data completamento</th>
            <th>In errore</th>
            <th>Tentativo</th>
            <th>Tentativi rimasti</th>
            <th>Destinatario</th>
            <th>Trace&nbsp;ID</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="ev in historyData" :key="ev.TraceId">
            <td>{{ ev.Creazione }}</td>
            <td>{{ ev.Tipo }}</td>
            <td>{{ ev.Completato }}</td>
            <td>{{ ev.CompletatoIl }}</td>
            <td>{{ ev.Errore }}</td>
            <td>{{ ev.Tentativo }}</td>
            <td>{{ ev.TentativiRimasti }}</td>
            <td>{{ ev.Destinatario }}</td>
            <td>{{ ev.TraceId }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import ApiService from "@/common/api.service";

export default {
  name: 'FormDocumentHistorySummary',
  props: {
    submissionId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      historyData: null,
      rawHistoryEvents: null
    };
  },
  computed: {
    /**
     * Check if we should show the summary
     * Only show if we have recognizable events
     */
    showSummary() {
      if (!this.rawHistoryEvents || this.rawHistoryEvents.length === 0) return false;

      // Check if we have any recognizable event types
      const recognizableTypes = [
        'SendSignNotificationToRecipient',
        'FinishDocument',
        'CreateSequenceWorkstep'
      ];

      const hasRecognizableEvents = this.rawHistoryEvents.some(ev =>
        recognizableTypes.includes(ev.Type)
      );

      return hasRecognizableEvents;
    },

    /**
     * Extract unique recipients from events
     */
    recipients() {
      if (!this.rawHistoryEvents) return [];

      const recipientMap = new Map();

      this.rawHistoryEvents.forEach(event => {
        if (event.AffectedRecipient && event.AffectedRecipient.Id) {
          const recipient = event.AffectedRecipient;
          const id = recipient.Id;

          if (!recipientMap.has(id)) {
            recipientMap.set(id, {
              id: id,
              name: `${recipient.GivenName || ''} ${recipient.Surname || ''}`.trim(),
              email: recipient.Email,
              hasSigned: false
            });
          }

          // Check if this recipient has signed (FinishDocument event)
          if (event.Type === 'FinishDocument' && event.Completed) {
            recipientMap.get(id).hasSigned = true;
          }
        }
      });

      return Array.from(recipientMap.values());
    },

    /**
     * Get the last notification date
     */
    lastNotificationDate() {
      if (!this.rawHistoryEvents) return null;

      const notificationEvents = this.rawHistoryEvents
        .filter(ev => ev.Type === 'SendSignNotificationToRecipient' && ev.CompletedOn)
        .sort((a, b) => new Date(b.CompletedOn) - new Date(a.CompletedOn));

      if (notificationEvents.length > 0) {
        return moment(notificationEvents[0].CompletedOn).locale('it').format('DD/MM/YYYY, HH:mm');
      }

      return null;
    },

    /**
     * Get the document completion date
     */
    completionDate() {
      if (!this.rawHistoryEvents) return null;

      const finishEvents = this.rawHistoryEvents
        .filter(ev => ev.Type === 'FinishDocument' && ev.Completed)
        .sort((a, b) => new Date(b.CompletedOn) - new Date(a.CompletedOn));

      if (finishEvents.length > 0) {
        return moment(finishEvents[0].CompletedOn).locale('it').format('DD/MM/YYYY, HH:mm');
      }

      return null;
    },

    /**
     * Calculate statistics
     */
    stats() {
      const signed = this.recipients.filter(r => r.hasSigned).length;
      const pending = this.recipients.filter(r => !r.hasSigned).length;

      return {
        signed,
        pending
      };
    }
  },
  methods: {
    async loadHistory() {
      this.historyData = null;
      this.rawHistoryEvents = null;

      try {
        const { data } = await ApiService.get(
          `submissions/${this.submissionId}/history`
        );

        // Store raw events for summary component
        this.rawHistoryEvents = data?.Events ?? [];

        /* ─────────────── Traduzioni & formatter ─────────────── */
        const tipoIT = {
          ApiCallbackFinishedEnvelope: 'Callback API completato',
          SendSignNotificationToRecipient: 'Notifica inviata',
          FinishDocument: 'Documento completato',
          CreateSequenceWorkstep: 'Crea sequenza di lavoro',
        };
        const toTipoIT = (t) => tipoIT[t] ?? t;
        const toDateIT = (iso) =>
          iso ? moment(iso).locale('it').format('L, LT') : '—';
        const toBoolIT = (v) => (v ? 'Sì' : 'No');

        /* ─────────────── Ordinamento e mapping ─────────────── */
        this.historyData = (data?.Events ?? [])
          .slice()
          .sort((a, b) => new Date(b.CreationDate) - new Date(a.CreationDate))
          .map((ev) => ({
            Creazione: toDateIT(ev.CreationDate),
            Tipo: toTipoIT(ev.Type),
            Completato: toBoolIT(ev.Completed),
            CompletatoIl: toDateIT(ev.CompletedOn),
            Errore: toBoolIT(ev.IsInErrorState),
            Tentativo: ev.Attempt,
            TentativiRimasti: ev.RemainingAttempts,
            Destinatario: ev.AffectedRecipient
              ? `${ev.AffectedRecipient.GivenName ?? ''} ${ev.AffectedRecipient.Surname ?? ''}`.trim() || '—'
              : '—',
            Email: ev.AffectedRecipient?.Email ?? '—',
            TraceId: ev.TraceId,
          }));
      } catch (err) {
        console.error('Errore nel caricamento dello storico', err);
        this.historyData = [];
      }
    }
  }
};
</script>

<style scoped>
.history-summary {
  margin-top: 20px;
}

.card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-header h6 {
  font-weight: 600;
}

.recipient-item:last-child {
  border-bottom: none !important;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

.badge {
  padding: 6px 12px;
  font-size: 0.875rem;
}

.stat-box h4 {
  font-weight: bold;
}

.border-bottom {
  border-bottom: 1px solid #e9ecef;
}
</style>
